import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { SophiaSchedulesPanel } from "@/components/SophiaSchedulesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Loader2, Users, Network, ScrollText, KeyRound, ListChecks, Mail, Copy, X, DollarSign, BadgeCheck, PhoneOff } from "lucide-react";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { AgencyBilling } from "@/components/BillingManager";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// Assignable roles use the app's stored (legacy) values with canonical display labels.
// Deprecated/canonical stored values are normalized on read (temporary backward-compat).
const ROLE_OPTIONS = [
  { value: "admin", label: "Agency Admin" },
  { value: "ops_manager", label: "Manager" },
  { value: "agent", label: "Agent" },
  { value: "bpo", label: "Processor" },
  { value: "bpo_closer", label: "BPO Intake + Calling" },
  { value: "future", label: "Auditor" },
];
const CANON_TO_LEGACY = { agency_admin: "admin", manager: "ops_manager", processor: "bpo", auditor: "future", agent: "agent", vendor: "vendor" };
const legacyRole = (r) => CANON_TO_LEGACY[r] || r;
const ROLE_LABEL = { vendor: "Vendor (portal)", platform_owner: "Platform Owner", supervisor: "Supervisor",
  ...Object.fromEntries(ROLE_OPTIONS.map((o) => [o.value, o.label])) };

function StepUpDialog({ open, onClose, onGranted }) {
  const { t } = useTranslation();
  const [pwd, setPwd] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/agency/step-up", { password: pwd, mfa_code: code.trim() });
      onGranted(data.step_up_id); setPwd(""); setCode("");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("agency_admin.stepup_failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4" data-testid="stepup-dialog">
      <div className="w-full max-w-sm space-y-3 rounded-xl border bg-card p-5">
        <div className="flex items-center gap-2 font-semibold"><KeyRound className="h-4 w-4 text-primary" /> {t("agency_admin.stepup_title")}</div>
        <p className="text-xs text-muted-foreground">{t("agency_admin.stepup_hint")}</p>
        <Input type="password" placeholder={t("agency_admin.stepup_password")} value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="stepup-pwd" />
        <Input placeholder={t("agency_admin.stepup_code")} value={code} onChange={(e) => setCode(e.target.value)} data-testid="stepup-code" />
        <div className="flex gap-2">
          <Button size="sm" onClick={submit} disabled={busy} data-testid="stepup-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("agency_admin.confirm")}</Button>
          <Button size="sm" variant="outline" onClick={onClose}>{t("agency_admin.cancel")}</Button>
        </div>
      </div>
    </div>
  );
}

function ActivationLinkModal({ link, status, onClose }) {
  const { t } = useTranslation();
  const pending = status && status.status !== "sent";
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4" data-testid="aa-activation-modal">
      <div className="w-full max-w-md space-y-3 rounded-xl border bg-card p-5 text-sm">
        <div className="flex items-center justify-between font-semibold"><span>{t("agency_admin.activation_title")}</span>
          <button onClick={onClose} data-testid="aa-activation-close"><X className="h-4 w-4" /></button></div>
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800" data-testid="aa-email-status">
          {pending ? t("agency_admin.activation_pending") : t("agency_admin.activation_sent")}
        </div>
        <div className="flex items-center gap-2">
          <Input readOnly value={link} data-testid="aa-activation-input" className="text-xs" />
          <Button size="sm" variant="outline" data-testid="aa-activation-copy"
            onClick={() => { navigator.clipboard?.writeText(link).then(() => toast.success(t("agency_admin.copied"))).catch(() => {}); }}><Copy className="h-4 w-4" /></Button>
        </div>
        <div className="flex justify-end"><Button size="sm" onClick={onClose} data-testid="aa-activation-done">{t("agency_admin.done")}</Button></div>
      </div>
    </div>
  );
}

function InviteDialog({ open, onClose, onInvited }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("agent");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/agency/users/invite", { name, email, role });
      onInvited(data); setName(""); setEmail(""); setRole("agent");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("agency_admin.invite_failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4" data-testid="aa-invite-dialog">
      <div className="w-full max-w-sm space-y-3 rounded-xl border bg-card p-5">
        <div className="font-semibold">{t("agency_admin.invite_title")}</div>
        <Input placeholder={t("agency_admin.full_name")} value={name} onChange={(e) => setName(e.target.value)} data-testid="aa-invite-name" />
        <Input placeholder={t("agency_admin.email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="aa-invite-email" />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="aa-invite-role"><SelectValue /></SelectTrigger>
          <SelectContent>{ROLE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button size="sm" onClick={submit} disabled={busy} data-testid="aa-invite-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("agency_admin.send_invitation")}</Button>
          <Button size="sm" variant="outline" onClick={onClose}>{t("agency_admin.cancel")}</Button>
        </div>
      </div>
    </div>
  );
}

const BLANK_IDENTITY = {
  display_name: "", agency_name: "", role_title: "", callback_number: "",
  sms_signature: "", email_signature: "", email_sender_ref: "", live_transfer_destination: "",
};

function CommunicationIdentitiesTab() {
  const [identities, setIdentities] = useState([]);
  const [form, setForm] = useState(BLANK_IDENTITY);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = () => api.get("/communication-identities").then(({ data }) => setIdentities(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.display_name.trim() || !form.agency_name.trim()) {
      toast.error("Display name and agency name are required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/communication-identities", form);
      toast.success("Communication identity created — verify it before it can be used");
      setForm(BLANK_IDENTITY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create communication identity");
    } finally {
      setCreating(false);
    }
  };

  const verify = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/communication-identities/${id}/verify`);
      toast.success("Communication identity verified");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not verify communication identity");
    } finally {
      setBusyId("");
    }
  };

  const setActive = async (id, active) => {
    setBusyId(id);
    try {
      await api.post(`/communication-identities/${id}/active`, { active });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update communication identity");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4" data-testid="aa-comm-identities">
      <div>
        <div className="font-medium">Communication Identities</div>
        <p className="text-xs text-muted-foreground">
          Who Sophia voice, SMS, and email disclose themselves as. Never derived from the logged-in
          user — an authorized human must create and verify each identity before it can be selected
          for an outbound call, text, or email.
        </p>
      </div>

      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-xs text-muted-foreground">
          <th className="py-2">Display Name</th><th>Agency Name</th><th>Callback</th><th>Verified</th><th>Active</th><th>Actions</th>
        </tr></thead>
        <tbody>
          {identities.map((ident) => (
            <tr key={ident.id} className="border-b text-xs" data-testid={`aa-identity-${ident.id}`}>
              <td className="py-2">{ident.display_name}<div className="text-muted-foreground">{ident.role_title}</div></td>
              <td>{ident.agency_name}</td>
              <td>{ident.callback_number || "—"}</td>
              <td>{ident.verified_at
                ? <span className="text-emerald-600">Verified {new Date(ident.verified_at).toLocaleDateString()}</span>
                : <span className="text-amber-600">Not verified</span>}</td>
              <td>{ident.active === false ? <span className="text-red-600">Inactive</span> : <span className="text-emerald-600">Active</span>}</td>
              <td className="space-x-1 py-1">
                {!ident.verified_at && (
                  <Button size="sm" variant="outline" disabled={busyId === ident.id}
                    onClick={() => verify(ident.id)} data-testid={`aa-identity-verify-${ident.id}`}>
                    Verify
                  </Button>
                )}
                <Button size="sm" variant="outline" disabled={busyId === ident.id}
                  onClick={() => setActive(ident.id, ident.active === false)}
                  data-testid={`aa-identity-toggle-${ident.id}`}>
                  {ident.active === false ? "Activate" : "Deactivate"}
                </Button>
              </td>
            </tr>
          ))}
          {identities.length === 0 && (
            <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">No communication identities yet.</td></tr>
          )}
        </tbody>
      </table>

      <div className="max-w-2xl space-y-2 border-t pt-4">
        <div className="text-sm font-medium">New identity</div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div><Label className="text-xs">Display Name</Label>
            <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="aa-identity-display-name" /></div>
          <div><Label className="text-xs">Agency Name</Label>
            <Input value={form.agency_name} onChange={(e) => setForm({ ...form, agency_name: e.target.value })} data-testid="aa-identity-agency-name" /></div>
          <div><Label className="text-xs">Role / Title</Label>
            <Input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} /></div>
          <div><Label className="text-xs">Callback Number</Label>
            <Input value={form.callback_number} onChange={(e) => setForm({ ...form, callback_number: e.target.value })} placeholder="10-digit" /></div>
          <div><Label className="text-xs">Live-Transfer Destination</Label>
            <Input value={form.live_transfer_destination} onChange={(e) => setForm({ ...form, live_transfer_destination: e.target.value })} placeholder="10-digit" /></div>
          <div><Label className="text-xs">Email Sender Reference</Label>
            <Input value={form.email_sender_ref} onChange={(e) => setForm({ ...form, email_sender_ref: e.target.value })} /></div>
          <div><Label className="text-xs">SMS Signature</Label>
            <Input value={form.sms_signature} onChange={(e) => setForm({ ...form, sms_signature: e.target.value })} /></div>
          <div><Label className="text-xs">Email Signature</Label>
            <Input value={form.email_signature} onChange={(e) => setForm({ ...form, email_signature: e.target.value })} /></div>
        </div>
        <Button onClick={create} disabled={creating} data-testid="aa-identity-create">
          {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Create Identity
        </Button>
      </div>
    </div>
  );
}

const US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern (America/New_York)" },
  { value: "America/Chicago", label: "Central (America/Chicago)" },
  { value: "America/Denver", label: "Mountain (America/Denver)" },
  { value: "America/Phoenix", label: "Arizona — no DST (America/Phoenix)" },
  { value: "America/Los_Angeles", label: "Pacific (America/Los_Angeles)" },
  { value: "America/Anchorage", label: "Alaska (America/Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Pacific/Honolulu)" },
];

function SophiaControlsTab() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tzInput, setTzInput] = useState("");
  const [startHourInput, setStartHourInput] = useState("8");
  const [endHourInput, setEndHourInput] = useState("20");
  const [maxActiveInput, setMaxActiveInput] = useState("4");
  const [hoursBusy, setHoursBusy] = useState(false);

  const load = () => api.get("/voice-ai/auto/status").then(({ data }) => {
    setStatus(data);
    setTzInput(data.agency_timezone || "America/New_York");
    setStartHourInput(String(data.call_window?.start ?? 8));
    setEndHourInput(String(data.call_window?.end ?? 20));
    setMaxActiveInput(String(data.max_active ?? 4));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveCallingHours = async () => {
    setHoursBusy(true);
    try {
      await api.put("/agency/profile", {
        timezone: tzInput,
        call_window_start_hour: parseInt(startHourInput, 10),
        call_window_end_hour: parseInt(endHourInput, 10),
        max_concurrent_calls: parseInt(maxActiveInput, 10),
      });
      toast.success("Timezone, calling hours, and concurrent-call limit updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update calling hours");
    } finally {
      setHoursBusy(false);
    }
  };

  const pause = async () => {
    setBusy(true);
    try {
      await api.post("/voice-ai/auto/pause");
      toast.success("New Sophia calls are paused for this agency. Active calls are unaffected.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not pause Sophia calling");
    } finally { setBusy(false); }
  };

  const resume = async () => {
    setBusy(true);
    try {
      await api.post("/voice-ai/auto/resume");
      toast.success("Sophia calling resumed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not resume Sophia calling");
    } finally { setBusy(false); }
  };

  const cancelQueued = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/voice-ai/auto/cancel-queued");
      toast.success(`Canceled ${data.canceled} queued Sophia call(s). ${data.still_active} active call(s) still need End Active Call individually.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not cancel queued Sophia calls");
    } finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4" data-testid="aa-sophia-controls">
      <div>
        <div className="font-medium">Sophia Emergency Controls</div>
        <p className="text-xs text-muted-foreground">
          Pause stops new Sophia dialing immediately — it does NOT hang up any call already in
          progress. Cancel Queued removes calls that have not started dialing yet, and never
          touches an active call. Each active call must be ended individually from its own live
          call panel.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div>
          Status:{" "}
          {status.emergency_paused
            ? <span className="font-medium text-red-600">PAUSED — no new Sophia calls</span>
            : <span className="font-medium text-emerald-600">Not paused</span>}
        </div>
        <div className="text-muted-foreground">Active calls right now: <span className="font-medium text-foreground">{status.active_calls}</span></div>
      </div>

      <div className="flex flex-wrap gap-2">
        {status.emergency_paused ? (
          <Button onClick={resume} disabled={busy} data-testid="aa-sophia-resume">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Resume Sophia Calling
          </Button>
        ) : (
          <Button variant="destructive" onClick={pause} disabled={busy} data-testid="aa-sophia-pause">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PhoneOff className="mr-1.5 h-4 w-4" />} Pause New Sophia Calls
          </Button>
        )}
        <Button variant="outline" onClick={cancelQueued} disabled={busy} data-testid="aa-sophia-cancel-queued">
          Cancel Queued Calls
        </Button>
      </div>

      <div className="border-t pt-4">
        <div className="font-medium">Calling Hours &amp; Timezone</div>
        <p className="mt-1 text-xs text-muted-foreground">
          Sophia only dials automatically within this local window. Hours are clamped to
          8:00–21:00 (the TCPA-permitted window for outbound calls to a residential number) and
          apply to the timezone selected below.
        </p>
        <div className="mt-2 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Timezone</Label>
            <Select value={tzInput} onValueChange={setTzInput}>
              <SelectTrigger className="mt-1 w-64" data-testid="aa-sophia-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>
                {US_TIMEZONES.map((tz) => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Start hour (0-23)</Label>
            <Input type="number" min={8} max={20} className="mt-1 w-24" value={startHourInput}
              onChange={(e) => setStartHourInput(e.target.value)} data-testid="aa-sophia-start-hour" />
          </div>
          <div>
            <Label className="text-xs">End hour (1-21)</Label>
            <Input type="number" min={9} max={21} className="mt-1 w-24" value={endHourInput}
              onChange={(e) => setEndHourInput(e.target.value)} data-testid="aa-sophia-end-hour" />
          </div>
          <div>
            <Label className="text-xs">Max simultaneous calls (1-25)</Label>
            <Input type="number" min={1} max={25} className="mt-1 w-32" value={maxActiveInput}
              onChange={(e) => setMaxActiveInput(e.target.value)} data-testid="aa-sophia-max-active" />
          </div>
          <Button onClick={saveCallingHours} disabled={hoursBusy} data-testid="aa-sophia-save-hours">
            {hoursBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Save
          </Button>
        </div>
        {status.call_window && (
          <p className="mt-2 text-xs text-muted-foreground">
            Currently configured: {String(status.call_window.start).padStart(2, "0")}:00–{String(status.call_window.end).padStart(2, "0")}:00,{" "}
            {status.agency_timezone || "America/New_York"}. Up to {status.max_active} call(s) at once.
          </p>
        )}
      </div>

      <div className="border-t pt-4">
        <SophiaSchedulesPanel />
        <p className="mt-2 text-xs text-muted-foreground">
          Full session status, results, recordings, and exports live on the{" "}
          <Link to="/sophia" className="underline">Sophia Operations dashboard</Link>.
        </p>
      </div>
    </div>
  );
}

export default function AgencyAdmin() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("team");
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [tn, setTn] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [agencyProfile, setAgencyProfile] = useState(null);
  const [emailData, setEmailData] = useState(null);
  const [invite, setInvite] = useState(false);
  const [activation, setActivation] = useState(null);
  const [stepUp, setStepUp] = useState(null); // pending action fn
  const [stepUpId, setStepUpId] = useState("");

  const loadUsers = () => api.get("/agency/users").then(({ data }) => setUsers(data)).catch(() => {});
  const loadEvents = () => api.get("/agency/security-events").then(({ data }) => setEvents(data)).catch(() => {});
  const loadTn = () => api.get("/agency/trusted-network").then(({ data }) => setTn(data.trusted_network || { enabled: false })).catch(() => {});
  const loadOnboarding = () => api.get("/agency/onboarding").then(({ data }) => { setOnboarding(data.onboarding); setAgencyProfile(data.agency); }).catch(() => {});
  const loadEmail = () => api.get("/agency/email-status").then(({ data }) => setEmailData(data)).catch(() => {});
  useEffect(() => { loadUsers(); loadEvents(); loadTn(); loadOnboarding(); loadEmail(); }, []);

  const resendInvite = async (u) => {
    try {
      const { data } = await api.post(`/agency/users/${u.id}/resend-invite`);
      setActivation({ link: data.invite_link, status: data.email_status });
      loadUsers();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("agency_admin.resend_failed")); }
  };
  const toggleMfaReq = async (v) => {
    try {
      await api.put("/agency/mfa-requirement", { require_mfa: v });
      toast.success(t("agency_admin.toast_mfa_updated")); loadOnboarding();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("agency_admin.toast_failed")); }
  };

  // run an action that needs step-up; opens dialog if no grant yet
  const withStepUp = (fn) => {
    if (stepUpId) { fn(stepUpId); return; }
    setStepUp(() => fn);
  };

  const changeRole = (u, role) => withStepUp(async (sid) => {
    try { await api.post(`/agency/users/${u.id}/role`, { role, step_up_id: sid }); toast.success(t("agency_admin.toast_role_updated")); loadUsers(); loadEvents(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  });
  const toggleActive = (u) => withStepUp(async (sid) => {
    try { await api.post(`/agency/users/${u.id}/active`, { active: !(u.active !== false), step_up_id: sid }); toast.success(t("agency_admin.toast_updated")); loadUsers(); loadEvents(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  });
  const revoke = (u) => withStepUp(async (sid) => {
    try { await api.post(`/agency/users/${u.id}/revoke-sessions`, { step_up_id: sid }); toast.success(t("agency_admin.toast_sessions_revoked")); loadEvents(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  });
  const resetMfa = (u) => withStepUp(async (sid) => {
    try { await api.post(`/agency/users/${u.id}/reset-mfa`, { step_up_id: sid }); toast.success(t("agency_admin.toast_mfa_reset")); loadUsers(); loadEvents(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  });

  const saveTn = () => withStepUp(async (sid) => {
    try {
      await api.put(`/agency/trusted-network?step_up_id=${sid}`, {
        ...tn,
        ipv4: typeof tn.ipv4 === "string" ? tn.ipv4.split(",").map((x) => x.trim()).filter(Boolean) : (tn.ipv4 || []),
        cidr: typeof tn.cidr === "string" ? tn.cidr.split(",").map((x) => x.trim()).filter(Boolean) : (tn.cidr || []),
        ipv6: tn.ipv6 || [],
      });
      toast.success(t("agency_admin.toast_tn_saved")); loadTn(); loadEvents();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  });

  const TABS = [["team", t("agency_admin.tabs.team"), Users], ["onboarding", t("agency_admin.tabs.onboarding"), ListChecks], ["billing", t("agency_admin.tabs.billing"), DollarSign], ["email", t("agency_admin.tabs.email"), Mail], ["network", t("agency_admin.tabs.network"), Network], ["events", t("agency_admin.tabs.events"), ScrollText], ["identities", "Communication Identities", BadgeCheck], ["sophia", "Sophia Controls", PhoneOff]];

  return (
    <div className="space-y-5" data-testid="agency-admin">
      <PageHeader title={t("agency_admin.title")} subtitle={t("agency_admin.subtitle")} />
      <div className="flex flex-wrap gap-2 border-b">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`aa-tab-${k}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm ${tab === k ? "border-b-2 border-primary font-semibold" : "text-muted-foreground"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "team" && (
        <div className="rounded-lg border bg-card p-4" data-testid="aa-team">
          <div className="mb-3 flex justify-end">
            <Button size="sm" data-testid="aa-invite-btn" onClick={() => setInvite(true)}>{t("agency_admin.invite_user")}</Button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2">{t("agency_admin.col_user")}</th><th>{t("agency_admin.col_role")}</th><th>{t("agency_admin.col_invite")}</th><th>{t("agency_admin.col_status")}</th><th>{t("agency_admin.col_last_login")}</th><th>{t("agency_admin.col_actions")}</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b" data-testid={`aa-user-${u.email}`}>
                  <td className="py-2">{u.name}<div className="text-xs text-muted-foreground">{u.email}</div></td>
                  <td>
                    {ROLE_OPTIONS.some((o) => o.value === legacyRole(u.role)) ? (
                      <Select value={legacyRole(u.role)} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger className="h-8 w-40" data-testid={`aa-role-${u.email}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-muted-foreground" data-testid={`aa-role-${u.email}`}>{ROLE_LABEL[u.role] || u.role}</span>
                    )}
                  </td>
                  <td className="text-xs" data-testid={`aa-invite-status-${u.email}`}>{u.invitation_status === "pending" ? <span className="text-amber-600">{t("agency_admin.status_pending")}</span> : (u.invitation_status === "active" ? <span className="text-emerald-600">{t("agency_admin.status_active")}</span> : "—")}</td>
                  <td>{u.active === false ? <span className="text-red-600">{t("agency_admin.status_disabled")}</span> : <span className="text-emerald-600">{t("agency_admin.status_active")}</span>}</td>
                  <td className="text-xs">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</td>
                  <td className="space-x-1 py-1">
                    <Button size="sm" variant="outline" onClick={() => resendInvite(u)} data-testid={`aa-resend-${u.email}`}>{t("agency_admin.resend")}</Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(u)} data-testid={`aa-toggle-${u.email}`}>{u.active === false ? t("agency_admin.reactivate") : t("agency_admin.disable")}</Button>
                    <Button size="sm" variant="outline" onClick={() => revoke(u)} data-testid={`aa-revoke-${u.email}`}>{t("agency_admin.revoke")}</Button>
                    <Button size="sm" variant="outline" onClick={() => resetMfa(u)} data-testid={`aa-resetmfa-${u.email}`}>{t("agency_admin.reset_mfa")}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "onboarding" && (
        <div className="max-w-2xl space-y-4 rounded-lg border bg-card p-4" data-testid="aa-onboarding">
          <div className="flex items-center justify-between rounded-md border bg-card/50 px-3 py-2 text-sm">
            <div>
              <div className="font-medium">{t("agency_admin.require_mfa")}</div>
              <div className="text-xs text-muted-foreground">{t("agency_admin.require_mfa_hint")}</div>
            </div>
            <Switch checked={agencyProfile?.require_mfa === true} onCheckedChange={toggleMfaReq} data-testid="aa-mfa-required" />
          </div>
          <OnboardingChecklist data={onboarding} testid="aa-onboarding-checklist" />
        </div>
      )}

      {tab === "billing" && <AgencyBilling />}

      {tab === "email" && emailData && (
        <div className="space-y-3 rounded-lg border bg-card p-4" data-testid="aa-email">
          <div className={`rounded-md px-3 py-2 text-sm ${emailData.provider.configured ? "bg-emerald-50 text-emerald-800" : "border border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="aa-email-provider">
            {emailData.provider.configured
              ? t("agency_admin.email_configured", { email: emailData.provider.from_email })
              : t("agency_admin.email_not_configured", { missing: emailData.provider.missing.join(", ") })}
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2">{t("agency_admin.col_time")}</th><th>{t("agency_admin.col_recipient")}</th><th>{t("agency_admin.col_type")}</th><th>{t("agency_admin.col_status")}</th></tr></thead>
            <tbody>
              {emailData.messages.map((m) => (
                <tr key={m.id} className="border-b text-xs">
                  <td className="py-1.5">{m.created_at ? new Date(m.created_at).toLocaleString() : "—"}</td>
                  <td>{m.recipient}</td><td>{m.kind}</td>
                  <td>{m.status === "sent" ? <span className="text-emerald-600">{t("agency_admin.email_sent")}</span> : <span className="text-amber-600">{m.status}</span>}</td>
                </tr>
              ))}
              {emailData.messages.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">{t("agency_admin.email_empty")}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "network" && tn && (
        <div className="max-w-xl space-y-3 rounded-lg border bg-card p-4" data-testid="aa-network">
          <div className="flex items-center justify-between">
            <Label>{t("agency_admin.tn_require")}</Label>
            <Switch checked={!!tn.enabled} onCheckedChange={(v) => setTn({ ...tn, enabled: v })} data-testid="aa-tn-enabled" />
          </div>
          <div><Label className="text-xs">{t("agency_admin.tn_ipv4")}</Label>
            <Input value={Array.isArray(tn.ipv4) ? tn.ipv4.join(", ") : (tn.ipv4 || "")} onChange={(e) => setTn({ ...tn, ipv4: e.target.value })} data-testid="aa-tn-ipv4" /></div>
          <div><Label className="text-xs">{t("agency_admin.tn_cidr")}</Label>
            <Input value={Array.isArray(tn.cidr) ? tn.cidr.join(", ") : (tn.cidr || "")} onChange={(e) => setTn({ ...tn, cidr: e.target.value })} data-testid="aa-tn-cidr" /></div>
          <div><Label className="text-xs">{t("agency_admin.tn_roles")}</Label>
            <Input value={Array.isArray(tn.roles) ? tn.roles.join(", ") : (tn.roles || "")} onChange={(e) => setTn({ ...tn, roles: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} data-testid="aa-tn-roles" /></div>
          <div><Label className="text-xs">{t("agency_admin.tn_mode")}</Label>
            <Select value={tn.mode || "block"} onValueChange={(v) => setTn({ ...tn, mode: v })}>
              <SelectTrigger className="w-40" data-testid="aa-tn-mode"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="block">{t("agency_admin.tn_block")}</SelectItem><SelectItem value="alert">{t("agency_admin.tn_alert")}</SelectItem></SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">{t("agency_admin.tn_note")}</p>
          <Button onClick={saveTn} data-testid="aa-tn-save">{t("agency_admin.tn_save")}</Button>
        </div>
      )}

      {tab === "events" && (
        <div className="rounded-lg border bg-card p-4" data-testid="aa-events">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2">{t("agency_admin.col_time")}</th><th>{t("agency_admin.col_event")}</th><th>{t("agency_admin.col_actor")}</th><th>{t("agency_admin.col_target")}</th><th>{t("agency_admin.col_ip")}</th><th>{t("agency_admin.col_ok")}</th></tr></thead>
            <tbody>
              {events.slice(0, 200).map((e, i) => (
                <tr key={i} className="border-b text-xs">
                  <td className="py-1.5">{e.timestamp ? new Date(e.timestamp).toLocaleString() : "—"}</td>
                  <td>{e.event}</td><td>{e.actor || "—"}</td><td>{e.target_user || "—"}</td><td>{e.ip || "—"}</td>
                  <td>{e.success ? "✓" : "✗"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "identities" && <CommunicationIdentitiesTab />}
      {tab === "sophia" && <SophiaControlsTab />}

      <StepUpDialog open={!!stepUp} onClose={() => setStepUp(null)}
        onGranted={(sid) => { setStepUpId(sid); const fn = stepUp; setStepUp(null); if (fn) fn(sid); }} />
      <InviteDialog open={invite} onClose={() => setInvite(false)}
        onInvited={(data) => { setInvite(false); loadUsers(); loadOnboarding(); setActivation({ link: data.invite_link, status: data.email_status }); }} />
      {activation && <ActivationLinkModal link={activation.link} status={activation.status} onClose={() => setActivation(null)} />}
    </div>
  );
}
